import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  TextField,
  InputAdornment,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import type {
  ValidCharacterItem,
  AvailableValidCharacterItem,
} from "../types/api-types";

interface ValidCharactersDialogProps {
  open: boolean;
  availableCharacters: AvailableValidCharacterItem[];
  selectedCharacters: ValidCharacterItem[];
  isSaving: boolean;
  onClose: () => void;
  onToggleCharacter: (character: AvailableValidCharacterItem) => void;
  onSave: () => void;
}

const ValidCharactersDialog = ({
  open,
  availableCharacters,
  selectedCharacters,
  isSaving,
  onClose,
  onToggleCharacter,
  onSave,
}: ValidCharactersDialogProps) => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredCharacters = (availableCharacters || []).filter((character) =>
    character?.value?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleClose = () => {
    setSearchTerm("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Select Valid Characters
          </Typography>
          <Chip
            label={`${selectedCharacters.length} selected`}
            size="small"
            color="primary"
          />
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box
          sx={{
            position: "sticky",
            top: 0,
            bgcolor: "background.paper",
            zIndex: 1,
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Search characters..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            p: 2,
            maxHeight: 400,
            overflowY: "auto",
          }}
        >
          {filteredCharacters.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ py: 3 }}
            >
              No characters found matching your search
            </Typography>
          ) : (
            filteredCharacters.map((character) => {
              const isSelected = selectedCharacters.some(
                (c) => c.asciiValue === character.id,
              );
              return (
                <FormControlLabel
                  key={`dialog-character-${character.id}`}
                  control={
                    <Checkbox
                      checked={isSelected}
                      onChange={() => onToggleCharacter(character)}
                      size="small"
                    />
                  }
                  label={character?.value || ""}
                  sx={{
                    m: 0,
                    p: 1,
                    borderRadius: 1,
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                />
              );
            })
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} color="inherit" disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={onSave} variant="contained" disabled={isSaving}>
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ValidCharactersDialog;
